import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtPct } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { KpiTile, SplitMeter } from "../components/intelligence/KpiTile";
import { CardMemoryTrigger } from "../components/card-memory/CardMemoryTrigger";
import { PageHeader } from "../components/PageHeader";
import { IconUser, IconInfo, IconChevronRight } from "../components/icons";
import { LoadMoreRow, useProgressiveList } from "../components/LoadMoreRow";
import type { TransportDriverCommissionDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

/** % (0..1 no DTO) → texto; "—" quando null. */
const pct = (v: number | null) => (v == null ? "—" : fmtPct(v * 100));

/**
 * Percentual do motorista se afasta da média em mais de 1 p.p.? Destaca na
 * tabela: comissão fora do padrão é o que vale conferir no acerto.
 */
const DEVIATION_PP = 0.01;

export function DriversPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "drivers", from, to],
    queryFn: () =>
      api.get<TransportDriverCommissionDto>(
        `/dashboards/transport/drivers?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    staleTime: 60_000,
  });

  const byDriver = data?.byDriver ?? [];
  const driverList = useProgressiveList(byDriver);
  const total = data?.totalCommission ?? 0;
  const maxCommission = Math.max(1, ...byDriver.map((d) => d.commission));
  const avgPct = data?.avgCommissionPct ?? null;
  const vazio = data != null && data.settlementCount === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Motoristas"
        subtitle="Comissão por motorista (acertos): base de fretes, percentual e ranking"
      />

      {dataError && <QueryErrorBanner onRetry={() => { dataRefetch(); }} />}

      {/* KPIs. Sem o selo "Atenção" sempre ligado na comissão (pagar comissão
          é o normal, não anomalia), mesmo critério das outras telas de Frota. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <CardMemoryTrigger memory={{ cardKey: "motoristas.comissao", from, to, expected: data?.totalCommission, title: "Comissão Total" }}>
          <KpiTile label="Comissão total" value={data ? data.totalCommissionFormatted : "—"}
            footnote={data && !vazio ? `Paga em ${fmtNumber(data.settlementCount)} acertos` : "Soma da comissão dos acertos"}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "motoristas.base-fretes", from, to, expected: data?.totalFreights, title: "Base de Fretes" }}>
          <KpiTile label="Base de fretes" value={data ? data.totalFreightsFormatted : "—"}
            footnote="Fretes dos acertos, base da comissão"
            visual={data && data.totalFreights > 0 ? (
              <SplitMeter parts={[
                { label: "Comissão", value: data.totalCommission, display: pct(data.totalCommission / data.totalFreights), tone: "danger" },
                { label: "Restante", value: data.totalFreights - data.totalCommission, display: pct(1 - data.totalCommission / data.totalFreights), tone: "muted" },
              ]} />
            ) : undefined}
            loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "motoristas.percentual", from, to, expected: data?.avgCommissionPct == null ? null : data.avgCommissionPct * 100, title: "Percentual Médio" }}>
          <KpiTile label="Percentual médio" value={data ? pct(data.avgCommissionPct) : "—"}
            footnote="Comissão ÷ base de fretes" loading={isLoading} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "motoristas.quantidade", from, to, expected: data?.driverCount, title: "Motoristas" }}>
          <KpiTile label="Motoristas" value={data ? fmtNumber(data.driverCount) : "—"}
            footnote={data && data.driverCount > 0
              ? `Média de ${fmtBRL(data.totalCommission / data.driverCount)} de comissão por motorista`
              : "Com acerto no período"}
            loading={isLoading} />
        </CardMemoryTrigger>
      </div>

      {/* Sem acertos: a tela explica o porquê e aponta onde está o custo de
          motorista, em vez de só "Sem acertos no período" com KPIs zerados. */}
      {vazio && (
        <div className="card p-6 flex flex-col sm:flex-row gap-4">
          <span className="w-10 h-10 rounded-full bg-info-50 text-info-600 flex items-center justify-center flex-shrink-0">
            <IconInfo className="w-5 h-5" />
          </span>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-900">Nenhum acerto de motorista no período</p>
            <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
              Esta tela soma a comissão registrada nos <strong>acertos</strong> do ERP. Quando a empresa não lança acertos,
              os números ficam zerados mesmo havendo viagens. O valor pago ao motorista por viagem (controle de viagens)
              aparece na coluna <strong>Salário Motorista</strong> da Margem por Placa.
            </p>
            <Link to="/frota/margem" className="inline-flex items-center gap-1 text-sm font-medium text-tm-700 hover:underline">
              Ver custo de motorista na Margem por Placa <IconChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}

      {/* Ranking por motorista */}
      {!vazio && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-start gap-2">
            <IconUser className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Comissão por motorista</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Percentual em destaque quando se afasta mais de 1 p.p. da média{avgPct != null ? ` (${pct(avgPct)})` : ""}
              </p>
            </div>
          </div>
          {isLoading ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-surface-border">
                    <th className="pl-5 pr-2 py-2 font-medium w-10">#</th>
                    <th className="px-2 py-2 font-medium">Motorista</th>
                    <th className="px-2 py-2 font-medium text-right">Acertos</th>
                    <th className="px-2 py-2 font-medium text-right">Base de fretes</th>
                    <th className="px-2 py-2 font-medium text-right">% comissão</th>
                    <th className="px-2 py-2 font-medium w-[26%]">Participação na comissão</th>
                    <th className="pl-2 pr-5 py-2 font-medium text-right">Comissão</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {driverList.visible.map((d, i) => {
                    const share = total > 0 ? (d.commission / total) * 100 : 0;
                    const fora = avgPct != null && d.commissionPct != null && Math.abs(d.commissionPct - avgPct) > DEVIATION_PP;
                    return (
                      <tr key={`${d.name}-${i}`} className="text-gray-700 hover:bg-surface-muted/50">
                        <td className="pl-5 pr-2 py-2.5 text-xs font-bold text-gray-400">{i + 1}</td>
                        <td className="px-2 py-2.5 font-medium text-gray-900 max-w-[260px] truncate" title={d.name}>{d.name}</td>
                        <td className="px-2 py-2.5 text-right">{fmtNumber(d.settlementCount)}</td>
                        <td className="px-2 py-2.5 text-right text-gray-500 whitespace-nowrap">{d.totalFreightsFormatted}</td>
                        <td className="px-2 py-2.5 text-right">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${fora ? "bg-warning-50 text-warning-700" : "text-gray-600"}`}
                            title={fora ? "Percentual fora da média dos motoristas" : undefined}>
                            {pct(d.commissionPct)}
                          </span>
                        </td>
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                              <div className="h-full rounded-full bg-tm-400" style={{ width: `${(d.commission / maxCommission) * 100}%` }} />
                            </div>
                            <span className="w-12 text-right text-xs text-gray-500">
                              {share.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                            </span>
                          </div>
                        </td>
                        <td className="pl-2 pr-5 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{d.commissionFormatted}</td>
                      </tr>
                    );
                  })}
                  <LoadMoreRow list={driverList} colSpan={7} label="motoristas" capped={false} />
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
